import { useEffect, useMemo, FC } from 'react'
import { useAtomValue } from 'jotai'
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil'
import { MapSigns } from '@emotion-icons/fa-solid/MapSigns'
import { Typography } from '@mui/material'
import styled from '@emotion/styled'
import { Colors, Shapes } from 'src/components/theme'
import AddressHeaderBG from 'src/assets/images/map-explorer/address-header.svg'
import { AvailableInsights } from 'src/components/sidebar-panels/available-insights'
import { PersonalInsights } from 'src/components/sidebar-panels/personal-insights'
import { SidebarHeader } from 'src/v2/components/sidebar-header'
import { SidebarHeaderContent } from 'src/v2/components/sidebar-header-content'
import { HeaderCover } from 'src/v2/components/sidebar-header-cover'
import { LocationDetails } from 'src/views/map-explorer/sidebar/panels/selected-location'
import { useMapExplorerNavigation } from 'src/views/map-explorer/sidebar/search/use-navigation'
import { UserListsPopover } from 'src/components/user-lists-popover'
import { SelectedEntityTree } from 'src/views/map-explorer/sidebar/related-results/selected-entity-tree'
import { FlexColumn } from 'src/components/flex-containers'
import { MapExplorerPanelActions } from 'src/views/map-explorer/sidebar/panels/actions'

import * as coreAtoms from 'src/v2/state/core'
import * as selectorAtoms from 'src/v2/state/map-explorer/selector'
import * as userListsPopoverAtoms from 'src/atoms/user-lists/user-lists-popover'
import * as userListsAtoms from 'src/atoms/user-lists'
import { ErrorBoundary } from 'src/components/errors/error-boundary'

const ReportsWrapper = styled(FlexColumn)`
  padding: ${Shapes.spacing[5]}px 0;
`

const ReportsTitle = styled(Typography)`
  padding: 0 ${Shapes.spacing[4]}px;
`

type MapExplorerSelectedAddressProps = {
  showNavigationActions?: boolean
}

export const MapExplorerSelectedAddress: FC<MapExplorerSelectedAddressProps> = ({ showNavigationActions = false }) => {
  const selectedEntity = useAtomValue(coreAtoms.selectedEntity)
  const setUserListsPopoverAnchorElement = useSetRecoilState(userListsPopoverAtoms.anchorElement)
  const [userListItem, setUserListItem] = useRecoilState(userListsAtoms.userListItem)
  const savedIn = useRecoilValue(userListsAtoms.savedIn(userListItem))
  const isSaved = useRecoilValue(userListsAtoms.isSaved(userListItem))
  const inEntitySelector = Boolean(useAtomValue(selectorAtoms.supportedSelections))
  const { clearSelected, getBackText, goBack } = useMapExplorerNavigation()

  const googleMapsUrl = useMemo(() => {
    if (!selectedEntity || selectedEntity.type !== 'SELECTED_ADDRESS') {
      return
    }

    const encodedName = encodeURIComponent(selectedEntity.data.name || '')
    const [lng, lat] = selectedEntity.data.geometry.location
    const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodedName}&query_place_id=${selectedEntity.data.place_id}&ll=${lat},${lng}`
    return mapsUrl
  }, [selectedEntity])

  const onClickSave = (evt: React.MouseEvent<HTMLElement>) => setUserListsPopoverAnchorElement(evt.currentTarget)

  useEffect(() => {
    if (!selectedEntity || selectedEntity.type !== 'SELECTED_ADDRESS') {
      return
    }
    setUserListItem({
      type: 'place',
      data: {
        googlePlaceId: selectedEntity.data.place_id,
        name: selectedEntity.data.name,
        address: selectedEntity.data.formatted_address,
      },
    })
  }, [selectedEntity])

  if (!selectedEntity || selectedEntity.type !== 'SELECTED_ADDRESS') {
    return null
  }

  const address = selectedEntity.data

  return (
    <>
      <ErrorBoundary wrap>
        <SidebarHeader
          cover={
            <>
              <HeaderCover
                icon={<MapSigns color={Colors.bgWhite} />}
                iconBackground={Colors.neutral400}
                iconBorderColor={Colors.neutral100}
                image={AddressHeaderBG}
                onClickSave={onClickSave}
                saved={isSaved}
                savedCount={savedIn.length}
                backText={showNavigationActions ? getBackText() : undefined}
                onBackClick={showNavigationActions ? goBack : undefined}
                onClose={showNavigationActions ? clearSelected : undefined}
              />
              <UserListsPopover />
            </>
          }
          content={
            <SidebarHeaderContent title={address.name}>
              <LocationDetails
                rating={address.rating}
                ratingCount={address.user_ratings_total}
                address={address.formatted_address}
                phone={address.phone}
                website={address.website}
                googleMapsUrl={googleMapsUrl}
                hideGoogleStreetView={inEntitySelector}
              />
              <SelectedEntityTree />
            </SidebarHeaderContent>
          }
        />
      </ErrorBoundary>
      {!inEntitySelector && (
        <div>
          <ErrorBoundary wrap>
            <MapExplorerPanelActions
              save={{
                onClick: onClickSave,
                isSaved,
              }}
              copy={{
                label: 'Address',
                text: address.formatted_address,
              }}
              streetView
              googleMapsUrl={googleMapsUrl}
            />
          </ErrorBoundary>
          <ReportsWrapper gap={Shapes.spacing[4]}>
            <ReportsTitle variant="title2" color={Colors.neutral950}>
              Reports
            </ReportsTitle>
            <ErrorBoundary wrap>
              <PersonalInsights />
            </ErrorBoundary>
            <ErrorBoundary wrap>
              <AvailableInsights />
            </ErrorBoundary>
          </ReportsWrapper>
        </div>
      )}
    </>
  )
}
